import type { RefObject } from 'react'
import { useEffect, useMemo, useState } from 'react'

interface OnScreenOptions {
  threshold?: number | number[]
  onIntersect?: ({ entry }: { entry: IntersectionObserverEntry }) => void
}

/**
 * Custom hook that detects whether an element is visible on the screen.
 * @param ref - The ref object of the element to observe.
 * @param options - Optional object with further configuration options.
 * @returns A boolean value indicating whether the element is intersecting with the viewport.
 */
export function useOnScreen(ref: RefObject<HTMLElement | null>, options?: OnScreenOptions) {
  const [isIntersecting, setIsIntersecting] = useState(false)

  const observer = useMemo(() => {
    if (typeof IntersectionObserver === 'undefined') {
      return null
    }
    return new IntersectionObserver(
      ([entry]) => {
        options?.onIntersect?.({ entry })
        setIsIntersecting(entry.isIntersecting)
      },
      {
        threshold: options?.threshold ?? 0,
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- The ref dependency is needed.
  }, [ref])

  useEffect(() => {
    if (ref.current && observer) {
      observer.observe(ref.current)
    }
    return () => {
      if (observer) {
        observer.disconnect()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- The observer should only be created once
  }, [])

  return isIntersecting
}
